import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminSidebar from './AdminSidebar';
import DashboardOverview from './DashboardOverview';
import ApplicationsQueue from './ApplicationsQueue';
import UserManagement from './UserManagement';
import PaymentManagement from './PaymentManagement';
import DocumentVerification from './DocumentVerification';
import ReportsAnalytics from './ReportsAnalytics';
import AdminSettings from './AdminSettings';
import MandateManagement from './MandateManagement';
import './AdminDashboard.css';

const isAdminRole = (role?: string | null) =>
  role === 'admin' || role === 'super_admin';


type AdminView =
  | 'overview'
  | 'applications'
  | 'users'
  | 'payments'
  | 'documents'
  | 'mandates'
  | 'reports'
  | 'settings';

// Tablet/iPad/small (≤1280px): drawer closed by default, hamburger opens it. Large desktop only: sidebar visible.
const DRAWER_BREAKPOINT = 1280;

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const [currentView, setCurrentView] = useState<AdminView>('overview');
  const [useDrawer, setUseDrawer] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);

  useLayoutEffect(() => {
    const mql = window.matchMedia(`(min-width: ${DRAWER_BREAKPOINT + 1}px)`);
    const update = () => {
      const isLargeDesktop = mql.matches;
      setUseDrawer(!isLargeDesktop);
      if (isLargeDesktop) {
        setSidebarCollapsed(false);
      } else {
        setSidebarCollapsed(true);
      }
    };
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/');
    setShowUserMenu(false);
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showUserMenu && !target.closest('.admin-user-menu')) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUserMenu]);

  const closeDrawer = () => setSidebarCollapsed(true);

  const handleViewChange = (view: AdminView) => {
    setCurrentView(view);
    if (useDrawer) closeDrawer();
  };

  // Render current view component
  const renderCurrentView = () => {
    switch (currentView) {
      case 'overview':
        return <DashboardOverview onNavigate={handleViewChange} />;
      case 'applications':
        return <ApplicationsQueue />;
      case 'users':
        return <UserManagement />;
      case 'payments':
        return <PaymentManagement />;
      case 'documents':
        return <DocumentVerification />;
      case 'mandates':
        return <MandateManagement />;
      case 'reports':
        return <ReportsAnalytics />;
      case 'settings':
        return <AdminSettings />;
      default:
        return <DashboardOverview />;
    }
  };

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  // Auth still hydrating — keep copy neutral (students must never see "admin permissions")
  if (!user) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAdminRole(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="admin-dashboard">
      {/* Admin Sidebar */}
      <AdminSidebar
        currentView={currentView}
        onViewChange={handleViewChange}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
        isMobileDrawer={useDrawer}
      />

      {/* Main Content Area */}
      <div className={`admin-main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {/* Header */}
        <div className="admin-header">
          <div className="header-left">
            <button
              className="sidebar-toggle-btn"
              onClick={() => setSidebarCollapsed((prev) => !prev)}
              type="button"
              aria-label={sidebarCollapsed ? 'Open menu' : 'Close menu'}
            >
              <span className="hamburger-icon" aria-hidden>☰</span>
            </button>
            <h1 className="admin-title">Admin</h1>
          </div>
          
          <div className="header-right">
            <div className="admin-user-menu">
              <button
                className="admin-user-info"
                onClick={() => setShowUserMenu(!showUserMenu)}
                type="button"
              >
                <span className="admin-welcome">Welcome back, {user?.first_name || 'Admin'}</span>
                <span className="admin-welcome-short" aria-hidden>Admin</span>
                <div className="admin-avatar">
                  <span className="avatar-text">{user?.first_name?.[0]?.toUpperCase() || user?.last_name?.[0]?.toUpperCase() || 'A'}</span>
                </div>
              </button>
              
              {showUserMenu && (
                <div className="user-menu-dropdown">
                  <button
                    className="user-menu-header"
                    onClick={() => {
                      setCurrentView('settings');
                      setShowUserMenu(false);
                    }}
                    type="button"
                  >
                    <div className="user-menu-avatar">
                      <span className="avatar-text">{user?.first_name?.[0]?.toUpperCase() || user?.last_name?.[0]?.toUpperCase() || 'A'}</span>
                    </div>
                    <div className="user-menu-info">
                      <div className="user-menu-name">{user?.first_name} {user?.last_name}</div>
                      <div className="user-menu-email">{user?.email || user?.phone_number}</div>
                    </div>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: 'auto', opacity: 0.5 }}>
                      <path d="M9 18l6-6-6-6"/>
                    </svg>
                  </button>
                  <div className="user-menu-divider"></div>
                  <button
                    className="user-menu-item"
                    onClick={() => {
                      setCurrentView('settings');
                      setShowUserMenu(false);
                    }}
                    type="button"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                    Profile & Settings
                  </button>
                  <div className="user-menu-divider"></div>
                  <button
                    className="user-menu-item logout-item"
                    onClick={handleLogout}
                    type="button"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                      <polyline points="16 17 21 12 16 7"/>
                      <line x1="21" y1="12" x2="9" y2="12"/>
                    </svg>
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Brand accent bar */}
        <div className="admin-accent-bar"></div>

        {/* Content Area */}
        <div className="admin-content">
          {renderCurrentView()}
        </div>
      </div>

      {/* Mobile overlay only when drawer is open */}
      {useDrawer && !sidebarCollapsed && (
        <div
          className="mobile-sidebar-overlay"
          role="button"
          tabIndex={0}
          aria-label="Close menu"
          onClick={(e) => {
            e.stopPropagation();
            closeDrawer();
          }}
          onPointerDown={(e) => {
            e.stopPropagation();
            closeDrawer();
          }}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
